import { ItemI, RoomSizes, SectionItemI } from "../../shared";
import { SectionInterface, StoreWallInterface } from "../types/store";
import { useStoreSystem } from "../useStoreSystem";
import { useStoreRoom } from "../useStoreRoom";
import { ShelvingInterface, useShelvingStore } from "../useShelvingStore";
import { Utils } from "../../utils/utils";
import { getSectionMinHeight } from "./getSectionMinHeight";
import { isBracket } from "../../utils/typeChecker";

export interface SpiritMap {
    id: string;
    points: number[];
}

const STEP_VALUE = 50;

//FIXME переименовать как переедем на новый стейт

function getSectionSlots(sectionIndex: number) {
    const currentShelf = useShelvingStore.getState().getCurrentShelving();
    const currentSection = currentShelf.sections[sectionIndex];
    const minHeight =
        (useStoreRoom.getState().height -
            currentShelf.offsetCeiling -
            Math.min(currentSection.leftRackHeight, currentSection.height)) /
        1000;
    const height = Math.min(currentSection.leftRackHeight, currentSection.height);

    return new Array(Math.ceil(height / STEP_VALUE))
        .fill(undefined)
        .map((value, index) => Math.trunc((minHeight + (STEP_VALUE / 1000) * index) * 1000) / 1000);
}

function reduceSlotsByHeight(slots: [number, boolean][], height: number) {
    return slots.reduce((acc, currentValue, currentIndex, array) => {
        const startSlot = currentValue[0];
        const endSlot = startSlot + height;

        // Проверяем предполагаемые занимаемые слоты
        const isClaimed = array.filter((slot) => slot[0] >= startSlot && slot[0] <= endSlot).some((slot) => slot[1]);

        // Ищем стартовый слот
        if (!isClaimed && acc.length === 0) {
            acc.push(startSlot);
            return acc;
        }

        // Проверяем чтобы не было колизии с прошлым слотом
        if (!isClaimed && acc[acc.length - 1] + height < startSlot) {
            acc.push(startSlot);
        }

        return acc;
    }, [] as number[]);
}

function getOriginItem(item: SectionItemI) {
    return useStoreSystem
        .getState()
        .itemsGroup.map((group) => group.ELEMENTS)
        .flat()
        .find((origin) => origin.BITRIX_OFFER_ID === item.bitrixId);
}

export function getHoldSlots(sections: SectionInterface[], sectionIndex: number): Array<[number, boolean]> {
    const section = sections[sectionIndex];
    const prevSection = sections[sectionIndex === 0 ? sectionIndex : sectionIndex - 1];
    const nextSection = sections[sectionIndex + 1];

    const sectionItemsYPos: Record<number, number> = {};
    const prevSectionItemsYPos: Record<number, number> = {};
    const nextSectionItemsYPos: Record<number, number> = {};

    const data = section.items.map((item) => {
        const origin = getOriginItem(item);
        const height = origin ? getOriginItemRealHeight(origin) : item.height;
        return {
            positionY: item.position[1],
            height: height / 1000,
        };
    });
    // const slots = getSectionSlots(sectionIndex);
    const currentShelving = useShelvingStore.getState().getCurrentShelving();
    // const connectors = [...currentShelving.connectorYPositions[section.id], ...currentShelving.connectorYPositions[prevSection.id]]

    /**
     * Добавляем занятые позиции
     */
    const connectorYPositions = currentShelving.connectorYPositions;
    let busyYPositions: number[] = [];
    if (connectorYPositions[section.id]) {
        busyYPositions.push(...connectorYPositions[section.id]);
    }
    if (connectorYPositions[section.id + "firstRack"]) {
        busyYPositions.push(...connectorYPositions[section.id + "firstRack"]);
    }
    if (connectorYPositions[prevSection.id]) {
        busyYPositions.push(...connectorYPositions[prevSection.id]);
    }
    section.items.forEach((item) => {
        const itemYPos = item.position[1];
        if (item.type?.includes?.("shelf")) {
            sectionItemsYPos[itemYPos] = itemYPos;
            busyYPositions.push(Utils.myRound(itemYPos - 0.05), Utils.myRound(itemYPos + 0.05));
        }
        busyYPositions.push(item.position[1]);
    });
    prevSection.items.forEach((item) => {
        const itemYPos = item.position[1];
        if (item.type?.includes?.("shelf")) {
            prevSectionItemsYPos[itemYPos] = itemYPos;
            busyYPositions.push(Utils.myRound(itemYPos - 0.05), Utils.myRound(itemYPos + 0.05));
        }
        busyYPositions.push(item.position[1]);
    });
    if (nextSection && !nextSection.isIndent) {
        nextSection.items.forEach((item) => {
            const itemYPos = item.position[1];
            if (item.type?.includes?.("shelf")) {
                nextSectionItemsYPos[itemYPos] = itemYPos;
                busyYPositions.push(Utils.myRound(itemYPos - 0.05), Utils.myRound(itemYPos + 0.05));
            }
            busyYPositions.push(item.position[1]);
        });
    }

    busyYPositions = Array.from(new Set(busyYPositions));

    const newHoldSlots: [number, boolean][] = [];

    /**
     * Получение свободных слотов, мест для добавляемого элемента
     */
    const roomState = useStoreRoom.getState();
    const shelvingState = useShelvingStore.getState();
    const getIsEmptySlots = (slot: number): void => {
        if (slot >= (roomState.height - shelvingState.getCurrentShelving().offsetCeiling) / 1000) {
            return;
        }
        const isClaimed = data.some((item) => {
            const min = item.positionY;
            const max = item.positionY + item.height;

            return slot >= min && slot <= max;
        });

        const isClaimedByConnector = busyYPositions
            .map((connector) => {
                return parseFloat(connector.toFixed(2));
            })
            /**
             * Фильтруем позиции на которые возможно поставить полки
             * TODO Melnikov: сейчас здесь константа 0.05 (это шаг на стойке) для полок работает все ровно но желательно бы добавить верхнюю и нижнюю границу элемента
             */
            .filter((connector) => {
                const nextConnector = Utils.myRound(connector + 0.05);
                const prevConnector = Utils.myRound(connector - 0.05);

                const isAvailableLeftYPos =
                    slot === connector &&
                    nextSectionItemsYPos[connector] &&
                    !prevSectionItemsYPos[connector] &&
                    !prevSectionItemsYPos[prevConnector] &&
                    !prevSectionItemsYPos[nextConnector];
                const isAvailableRightYPos =
                    slot === connector &&
                    !nextSectionItemsYPos[connector] &&
                    prevSectionItemsYPos[connector] &&
                    !nextSectionItemsYPos[prevConnector] &&
                    !nextSectionItemsYPos[nextConnector];
                // const isAvailableLeftAndRightYPos =
                //     slot === connector && !prevSectionItemsYPos[connector] && !nextSectionItemsYPos[connector] &&
                //     !prevSectionItemsYPos[prevConnector] && !prevSectionItemsYPos[nextConnector] &&
                //     !nextSectionItemsYPos[prevConnector] && !nextSectionItemsYPos[nextConnector] &&
                //     !sectionItemsYPos[prevConnector] && !sectionItemsYPos[nextConnector]

                return !(
                    (slot === connector && nextSectionItemsYPos[connector] && prevSectionItemsYPos[connector]) ||
                    isAvailableLeftYPos ||
                    isAvailableRightYPos
                );
            })
            .includes(slot);

        if (isClaimedByConnector || isClaimed) {
            return getIsEmptySlots(Utils.myRound(slot + 0.05));
        } else {
            newHoldSlots.push([slot, false]);
            getIsEmptySlots(Utils.myRound(slot + 0.05));
        }
    };

    const minHeight = Math.round((getSectionMinHeight(currentShelving, sectionIndex) + 0.05) * 10000) / 10000;
    getIsEmptySlots(minHeight);

    return newHoldSlots;
}

function getOriginItemParts(originItem: ItemI) {
    const parts = originItem.PARTS;
    return (
        useStoreSystem.getState()?.parts?.filter((part) => {
            return parts && parts.find((itemPart) => +part.ID === +itemPart && part.TYPE && isBracket(part.TYPE));
        }) ?? []
    );
}

function getOriginItemRealHeight(originItem: ItemI) {
    const parts = getOriginItemParts(originItem);
    const itemHeight = parseFloat(originItem.PROPERTIES?.HEIGHT || "100");
    if (parts.length > 0) {
        const partsSumHeight = Math.max(...parts.map((part) => parseFloat(part.PROPERTIES?.HEIGHT || "100")));
        return partsSumHeight / 1000;
    }
    return itemHeight / 1000;
}

interface getItemFillingRangesNewInterface {
    sections: SectionInterface[];
    item: ItemI;
}

export function getItemFillingRangesNew({ sections, item }: getItemFillingRangesNewInterface): SpiritMap[] {
    const itemWidth = parseInt(item.PROPERTIES.LENGTH);
    const itemHeight = getOriginItemRealHeight(item);
    const itemDepth = parseInt(item.PROPERTIES.DEPTH || "0");
    const isRod = item.TYPE?.length === 0 && item.TYPE?.[0] === "Штанга";
    /**
     * Для штанг - особая логика размещения только перед полками
     */
    if (isRod) {
        return sections
            .map((section, currentIndex) => {
                // Проверяем только секции с подходящей шириной
                if (itemWidth !== section.width) {
                    return { id: section.id, points: [] };
                }

                // Получаем все полки в текущей секции (исключая ghost элементы)
                const shelves = section.items.filter((item) => item.type?.includes("Полка") && !item.isSpirit);

                // Для каждой полки проверяем возможность размещения штанги
                const availablePoints: number[] = [];

                shelves.forEach((shelf) => {
                    // Проверяем, подходит ли длина полки для штанги
                    if (shelf.width === itemWidth) {
                        // Проверяем, нет ли уже штанги на этой полке
                        const existingRod = section.items.find(
                            (item) =>
                                item.type?.includes("Штанга") &&
                                Math.abs(item.position[1] - shelf.position[1]) < 0.01 &&
                                !item.isSpirit
                        );

                        // Если штанги нет, добавляем эту позицию
                        if (!existingRod) {
                            availablePoints.push(shelf.position[1]);
                        }
                    }
                });

                return {
                    id: section.id,
                    points: availablePoints,
                };
            })
            .filter((section) => !!section.points.length);
    }

    /**
     * Для элементов на одну секцию (не штанги)
     */
    if (itemWidth <= 800) {
        return sections
            .map((section, currentIndex) => {
                return {
                    id: section.id,
                    points:
                        itemWidth === section.width
                            ? reduceSlotsByHeight(getHoldSlots(sections, currentIndex), itemHeight)
                            : [],
                };
            })
            .filter((section) => !!section.points.length);
    } else {
        /**
         * Для элементов на две секции (не штанги)
         */
        const availableSections: Record<string, SectionInterface> = {};
        for (let i = 0; i < sections.length; ) {
            const section = sections[i];
            const nextSection = sections[i + 1];
            const sumSectionWidth = section.width + (nextSection ? nextSection.width : 0);
            const isAvailableWidth = sumSectionWidth === itemWidth;

            if (!isAvailableWidth) {
                i++;
                continue;
            }

            if (section.isIndent) {
                i++;
                continue;
            }
            if (nextSection && nextSection.isIndent) {
                i += 2;
                continue;
            }
            if (nextSection && !nextSection.isIndent) {
                availableSections[i] = section;
                i += 2;
            }
        }

        return Object.entries(availableSections)
            .map(([currentIndex, section]) => {
                return {
                    id: section.id,
                    points: reduceSlotsByHeight(getHoldSlots(sections, +currentIndex), itemHeight),
                };
            })
            .filter((section) => !!section.points.length);
    }
}

